import * as React from 'react'

import { Alert, Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  DocumentTemplateDetailsDocument,
  DocumentTemplateDetailsQuery,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { Authorized } from 'modules/core/components/Authorized'
import Modal from 'modules/core/components/Modal'
import Popover from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import FileList from 'modules/documents/components/FileList'
import type { DocumentTemplate } from 'modules/documents/hooks/useGetDocumentTemplates'
import { useUpdateDocumentTemplate } from 'modules/documents/hooks/useUpdateDocumentTemplate'

interface FormValues {
  file: File | undefined
}

type TaskConfigurationSendDocumentTemplate =
  DocumentTemplateDetailsQuery['processes']['taskConfigurationSendDocumentTemplatesConnection']['nodes'][number]

interface Props {
  documentTemplate: DocumentTemplate
  taskConfigurations?: TaskConfigurationSendDocumentTemplate[]
}

const renderAlertMessage = (type: 'warning' | 'error', children: React.ReactNode) => (
  <Box paddingTop="s24">
    <Alert color={type}>
      <Box gap="s4">
        <Text.Normal>{children}</Text.Normal>
      </Box>
    </Alert>
  </Box>
)

const SettingsButton = ({ documentTemplate, taskConfigurations }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const [isOpenedModal, setOpenedModal] = React.useState(false)
  const [templateFiles, setTemplateFiles] = React.useState<File[]>([])
  const { updateDocumentTemplate, loading } = useUpdateDocumentTemplate()

  const descriptionText = i18n.t('documents.document_templates.replace_file.description')

  const dropzoneText = documentTemplate.fillable
    ? i18n.t('documents.document_templates.replace_file.template_dropzone', {
        extension: 'PDF',
      })
    : i18n.t('documents.document_templates.replace_file.template_dropzone', {
        extension: 'DOCX',
      })

  const onReplace = () => {
    setTemplateFiles([])
    setOpenedModal(true)
  }

  const onCloseModal = () => {
    setOpenedModal(false)
  }

  const { handleSubmit, setValues, errors } = useForm<FormValues>({
    defaultValues: {
      file: undefined,
    },
    onSubmit: async (data, { setErrors }) => {
      const file = templateFiles[0]

      setValues({ file: templateFiles[0] })

      const result = await updateDocumentTemplate({
        id: documentTemplate.id!,
        file,
      })

      const handled = handleResult(result, (formErrors) => {
        setErrors({ file: formErrors.file || formErrors.errors || formErrors.base })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onCloseModal()
      }

      return handled
    },
  })

  const onDropFiles = (files: File[]) => {
    setTemplateFiles(files)
  }

  const onDeleteFile = (indexToRemove: number) => {
    setTemplateFiles(templateFiles.filter((_file, index) => index !== indexToRemove))
  }

  return (
    <>
      <Popover type="list">
        <Popover.Trigger
          aria-label={i18n.t('documents.document_templates.basic_info.options.title')}
        >
          <Button.Decorative type="secondary" iconName="ellipsis" />
        </Popover.Trigger>
        <Popover.Content side="bottom" align="end">
          <Popover.TouchableOption
            title={i18n.t('documents.document_templates.basic_info.options.replace_file')}
            onClick={onReplace}
          />
          <Popover.HrefOption
            href={documentTemplate.url!}
            title={i18n.t('documents.document_templates.basic_info.options.download_file')}
            download
          />
        </Popover.Content>
      </Popover>
      {isOpenedModal && (
        <Modal
          illustration="file-upload"
          title={i18n.t('documents.document_templates.replace_file.title')}
          description={descriptionText}
          onClose={onCloseModal}
          onSubmit={() => handleSubmit()}
          footer={
            <>
              <Button type="secondary" onClick={onCloseModal}>
                {i18n.t('common.cancel')}
              </Button>
              <Button loading={loading} disabled={!templateFiles.length || loading} submit>
                {i18n.t('documents.document_templates.replace_file.action')}
              </Button>
            </>
          }
        >
          {templateFiles.length ? (
            <FileList files={templateFiles} onDeleteFile={onDeleteFile} spaced={false} />
          ) : (
            <Authorized subscription={subscription}>
              <DropzoneBox
                onDrop={onDropFiles}
                multiple={false}
                accept={
                  documentTemplate.extension === '.pdf'
                    ? { 'application/pdf': [] }
                    : {
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                          [],
                      }
                }
                message={dropzoneText}
              />
            </Authorized>
          )}

          {errors.file && renderAlertMessage('error', errors.file)}

          {!!taskConfigurations?.length &&
            renderAlertMessage(
              'warning',
              <>
                {i18n.tp('documents.document_templates.integrations.replace_message', {
                  count: taskConfigurations.length,
                }) +
                  taskConfigurations
                    .map((taskConfiguration) => `"${taskConfiguration.title}"`)
                    .join(', ')}
              </>
            )}
        </Modal>
      )}
    </>
  )
}

export default observer(SettingsButton)
